import { insightConfigApi } from '../api/InsightConfigApi'
import type { InsightConfig, InsightConfigMeta } from '../models/insightConfig.types'
import {
  buildPatchPayload,
  extractEditablePayload,
  mapServerValidationErrors,
  type EditableInsightConfigPayload,
} from '../service/insightConfig'

import { ApiError } from '@/shared/http'

export type SaveInsightConfigMode = 'create' | 'patch' | 'replace'

export type SaveInsightConfigResult =
  | { kind: 'saved'; config: InsightConfig }
  | { kind: 'no_changes' }
  | { kind: 'validation'; errors: ReturnType<typeof mapServerValidationErrors> }

export type DeleteInsightConfigResult =
  { kind: 'deleted' } | { kind: 'blocked'; insightsCount: number }

interface InsightConfigApiPort {
  create: (payload: EditableInsightConfigPayload, signal?: AbortSignal) => Promise<InsightConfig>
  update: (
    id: number,
    payload: Partial<EditableInsightConfigPayload>,
    signal?: AbortSignal,
  ) => Promise<InsightConfig>
  replace: (
    id: number,
    payload: EditableInsightConfigPayload,
    signal?: AbortSignal,
  ) => Promise<InsightConfig>
  delete: (id: number, signal?: AbortSignal) => Promise<unknown>
}

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export function configInUseCount(payload: unknown): number {
  const body = record(payload)
  const error = record(body?.['error'])
  const details = record(error?.['details']) ?? record(body?.['details'])
  const value = details?.['insights_count'] ?? body?.['insights_count']
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : 0
}

export class SaveInsightConfigFlow {
  constructor(private readonly api: InsightConfigApiPort = insightConfigApi) {}

  async save(
    current: InsightConfig,
    mode: SaveInsightConfigMode,
    options: {
      original?: Partial<InsightConfig> | null
      meta?: Partial<InsightConfigMeta>
      signal?: AbortSignal
    } = {},
  ): Promise<SaveInsightConfigResult> {
    const payload = extractEditablePayload(current)
    try {
      if (mode === 'create') {
        return { kind: 'saved', config: await this.api.create(payload, options.signal) }
      }
      if (current.id === undefined) throw new Error('insights.sysadmin.config.idRequired')
      if (mode === 'replace') {
        return {
          kind: 'saved',
          config: await this.api.replace(current.id, payload, options.signal),
        }
      }

      const patch = buildPatchPayload(payload, options.original ?? {}, options.meta)
      if (Object.keys(patch).length === 0) return { kind: 'no_changes' }
      return {
        kind: 'saved',
        config: await this.api.update(current.id, patch, options.signal),
      }
    } catch (cause) {
      if (ApiError.isApiError(cause) && cause.kind === 'validation') {
        return { kind: 'validation', errors: mapServerValidationErrors(cause) }
      }
      throw cause
    }
  }

  async deleteConfig(id: number, signal?: AbortSignal): Promise<DeleteInsightConfigResult> {
    try {
      await this.api.delete(id, signal)
      return { kind: 'deleted' }
    } catch (cause) {
      if (ApiError.isApiError(cause) && cause.code === 'INSIGHT_CONFIG_IN_USE') {
        return { kind: 'blocked', insightsCount: configInUseCount(cause.payload) }
      }
      throw cause
    }
  }
}
